import { useNavigate } from 'react-router-dom';
import { cn } from '@/components/ui/utils';
import { InternBadge } from './shared/Badge';
import type { ProjectTask, TaskNote } from '../types';

interface Props {
  task: ProjectTask;
  latestNote?: TaskNote | null;
  onStatusChange: (taskId: string, status: string) => void;
}

const statusLabels: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  done: 'Done',
  blocked: 'Blocked',
};

const statusNext: Record<string, string> = {
  not_started: 'in_progress',
  in_progress: 'done',
  done: 'done',  // terminal
  blocked: 'in_progress',
};

export function TaskCard({ task, latestNote, onStatusChange }: Props) {
  const navigate = useNavigate();
  const escalation = task.status === 'done' ? 'done' : (task.escalation ?? 'regular');

  // Card style by escalation state
  const cardStyle = getCardStyle(escalation, task.is_critical);
  const isDone = task.status === 'done';

  return (
    <div
      className={cn(
        'rounded-[4px] border bg-white cursor-pointer group relative',
        'transition-all duration-200',
        'shadow-[0_2px_8px_rgba(27,58,45,0.08)] hover:shadow-[0_4px_16px_rgba(27,58,45,0.12)]',
        isDone && 'opacity-60',
        cardStyle.wrapper
      )}
      style={cardStyle.wrapperStyle}
      onClick={() => navigate(`/intern/task/${task.id}`)}
      role="article"
      aria-label={`Task: ${task.title}`}
    >
      {/* Left accent bar */}
      {cardStyle.accentBar && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 rounded-l-[4px]"
          style={{ background: cardStyle.accentBar }}
          aria-hidden
        />
      )}

      <div className={cn('p-5', cardStyle.accentBar && 'pl-6')}>
        {/* Top row: title + critical badge + status */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3
            className="text-[18px] font-[600] leading-snug flex-1"
            style={{ color: 'var(--color-near-black)', fontFamily: 'var(--font-sans)' }}
          >
            {task.title}
          </h3>
          <div className="flex items-center gap-2 shrink-0">
            {task.is_critical && (
              <InternBadge variant="critical" dot={false}>Critical</InternBadge>
            )}
            <StatusPill
              status={task.status}
              escalation={escalation}
              onClick={(e) => {
                e.stopPropagation();
                const next = statusNext[task.status] ?? 'in_progress';
                if (task.status !== 'done') onStatusChange(task.id, next);
              }}
            />
          </div>
        </div>

        {/* Description */}
        {task.description && (
          <p
            className="text-[15px] text-[var(--color-charcoal)] leading-relaxed line-clamp-2 mb-3"
          >
            {task.description}
          </p>
        )}

        {/* Blocked answer preview */}
        {task.status === 'blocked' && latestNote && (
          <div className="mb-3 px-3 py-2 rounded-[4px] bg-[rgba(154,41,41,0.06)] border border-[rgba(154,41,41,0.15)]">
            {latestNote.author !== 'worker' ? (
              <p className="text-[13px] text-[var(--color-charcoal)] line-clamp-2">
                <span className="font-[600] text-[var(--color-gold-dark)]">
                  {latestNote.author === 'claude' ? 'Claude' : 'Ken'}:
                </span>{' '}
                {latestNote.body}
                {latestNote.created_at && (
                  <span className="text-[var(--color-mid-gray)] ml-1">
                    ✓ answered {formatTime(latestNote.created_at)}
                  </span>
                )}
              </p>
            ) : (
              <p className="text-[13px] text-[var(--destructive)] font-[500]">
                Flagged — waiting on an answer
              </p>
            )}
          </div>
        )}

        {/* Date row */}
        <div className="flex items-center gap-4 flex-wrap">
          {task.due_date && (
            <div className="flex items-baseline gap-1.5">
              <span className="text-[12px] font-[500] text-[var(--color-mid-gray)] uppercase tracking-wide">Due</span>
              <span
                className={cn('text-[14px] font-[600]', escalation === 'red' && 'text-[var(--destructive)]')}
                style={escalation !== 'red' ? { color: 'var(--color-near-black)' } : undefined}
              >
                {formatDate(task.due_date)}
              </span>
              {task.due_in_days != null && task.status !== 'done' && (
                <DueInDays days={task.due_in_days} escalation={escalation} />
              )}
            </div>
          )}

          {task.go_live_date && (
            <div className="flex items-baseline gap-1.5">
              <span className="text-[12px] font-[500] text-[var(--color-mid-gray)] uppercase tracking-wide">Live</span>
              <span className="text-[14px] font-[600] text-[var(--color-near-black)]">
                {formatDate(task.go_live_date)}
              </span>
            </div>
          )}
        </div>

        {/* Footer: flag a blocker */}
        {task.status !== 'done' && (
          <div className="mt-3 flex justify-end" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => navigate(`/intern/task/${task.id}?focus=blocker`)}
              className="text-[13px] text-[var(--color-mid-gray)] hover:text-[var(--destructive)] transition-colors duration-200"
            >
              Flag a blocker
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Sub-components

function StatusPill({
  status,
  escalation,
  onClick,
}: {
  status: string;
  escalation: string;
  onClick: (e: React.MouseEvent) => void;
}) {
  const variant =
    status === 'done'
      ? 'completed'
      : status === 'blocked'
      ? 'blocked'
      : status === 'in_progress'
      ? 'active'
      : 'pending';

  return (
    <button
      onClick={onClick}
      className="transition-transform active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 rounded-[4px]"
      aria-label={`Status: ${statusLabels[status]}. Click to advance.`}
      disabled={status === 'done'}
    >
      <InternBadge variant={variant as any}>{statusLabels[status]}</InternBadge>
    </button>
  );
}

function DueInDays({ days, escalation }: { days: number; escalation: string }) {
  if (escalation === 'done') return null;
  const label = days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'due today' : `in ${days}d`;
  const color =
    escalation === 'red'
      ? 'var(--destructive)'
      : escalation === 'orange'
      ? 'var(--color-gold-dark)'
      : 'var(--color-mid-gray)';

  return (
    <span
      className="font-serif font-[600] text-[18px] leading-none"
      style={{ color, fontFamily: 'var(--font-serif)' }}
      aria-label={label}
    >
      {label}
    </span>
  );
}

// Helpers

function getCardStyle(escalation: string, isCritical: boolean) {
  if (isCritical || escalation === 'red') {
    return {
      wrapper: '',
      wrapperStyle: { background: 'rgba(154,41,41,0.04)', borderColor: 'rgba(154,41,41,0.2)' },
      accentBar: isCritical ? 'var(--destructive)' : 'var(--destructive)',
    };
  }
  if (escalation === 'orange') {
    return {
      wrapper: '',
      wrapperStyle: { background: 'rgba(184,150,62,0.06)', borderColor: 'rgba(184,150,62,0.25)' },
      accentBar: 'var(--color-gold)',
    };
  }
  return {
    wrapper: '',
    wrapperStyle: { background: 'white', borderColor: 'rgba(27,58,45,0.15)' },
    accentBar: null,
  };
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}
